import { API_BASE_URL } from './api';

export interface StaffMember {
  id?: number;
  user_id?: number; // Alternative ID field
  name: string;
  email: string;
  phone?: string;
  mobile?: string; // API uses 'mobile' instead of 'phone'
  country_code?: string; // Kept for backward compatibility
  sp_country_code?: string; // New field for staff country code
  status: 'active' | 'inactive' | 'suspended';
  role?: 'admin' | 'staff' | 'superadmin';
  created_at?: string;
  updated_at?: string;
  email_verified_at?: string | null;
  remember_token?: string | null;
  deleted_at?: string | null;
  fcm_token?: string;
  email_verified?: boolean;
  mobile_verified?: boolean;
  notification_enabled?: boolean;
  is_top_cleaner?: boolean | string;
}

const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  return response.json();
};

export const staffAPI = {
  /**
   * Get staff members with pagination
   */
  getAll: async (
    page: number = 1,
    limit: number = 10
  ): Promise<{ staff: StaffMember[]; total_records: number }> => {
    console.log('🔧 staffMember.ts getAll method called', { page, limit });

    try {
      console.log(`🔍 Fetching staff from: ${API_BASE_URL}/staff/list`);

      const response = await fetch(`${API_BASE_URL}/staff/list`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page,
          limit,
          search: '',
          sp_status: '',
          email_verified: '',
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Staff fetched successfully`);
        console.log('Raw response:', result);

        // Expecting structure similar to:
        // { data: { staff: [...], pagination: { total_records } } }
        const staffData =
          result.data?.staff ||
          result.data?.data ||
          result.data ||
          (Array.isArray(result) ? result : []);

        const total_records =
          result.data?.pagination?.total ||
          result.pagination?.total ||
          (Array.isArray(staffData) ? staffData.length : 0);

        if (Array.isArray(staffData)) {
          const mapped = staffData.map((user: any) => ({
            id: user.sp_id || user.id,
            name: user.sp_name || user.name,
            email: user.sp_email || user.email,
            phone: user.sp_mobile || user.mobile || user.phone,
            status: user.sp_status || user.status,
            user_type: user.user_type,
            created_at: user.created_at,
            updated_at: user.updated_at,
          }));

          return { staff: mapped, total_records };
        }

        console.log('No staff array found in response');
        return { staff: [], total_records: 0 };
      } else {
        const errorText = await response.text();
        console.log(`❌ Get staff failed: ${response.status} - ${errorText}`);
        throw new Error(`Failed to fetch staff: ${response.status}`);
      }
    } catch (error) {
      console.log(`❌ Error fetching staff:`, error);
      throw new Error('Failed to fetch staff members');
    }
  },

  /**
   * Create a new staff member
   */
  create: async (data: Omit<StaffMember, 'id' | 'created_at' | 'updated_at'> & { password: string }): Promise<StaffMember> => {
    console.log('🔧 staffMember.ts create method called');

    const requestData = {
      sp_name: data.name,
      sp_email: data.email,
      sp_mobile: data.mobile || data.phone || '',
      sp_country_code: data.sp_country_code || data.country_code || '', // Include sp_country_code
      password: data.password,
      fcm_token: data.fcm_token || '',
      sp_status: data.status || 'inactive',
      email_verified: data.email_verified || false,
      mobile_verified: data.mobile_verified || false,
      notification_enabled: data.notification_enabled || false
    };

    try {
      console.log(`🔍 Trying to create staff at: ${API_BASE_URL}/staff/create`);
      console.log('📝 Create data:', requestData);

      // Create FormData for multipart form (for profile photo uploads)
      const formData = new FormData();
      Object.keys(requestData).forEach(key => {
        if (requestData[key as keyof typeof requestData] !== null && requestData[key as keyof typeof requestData] !== undefined) {
          formData.append(key, requestData[key as keyof typeof requestData] as any);
        }
      });

      const response = await fetch(`${API_BASE_URL}/staff/create`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Staff created successfully`);

        // Handle response based on your API structure
        const userData = result.data || result;
        return {
          ...userData,
          id: userData.sp_id || userData.id,
          phone: userData.sp_mobile || userData.mobile || userData.phone
        };
      } else {
        const errorText = await response.text();
        console.log(`❌ Create failed: ${response.status} - ${errorText}`);
        throw new Error(`Create failed: ${response.status}`);
      }
    } catch (error) {
      console.log(`❌ Create failed:`, error);
      throw new Error('Failed to create staff member');
    }
  },

  /**
   * Update an existing staff member
   */
  update: async (id: number, data: Partial<StaffMember> & { password?: string }): Promise<StaffMember> => {
    console.log('🔧 staffMember.ts update method called with ID:', id);

    // Create FormData for multipart form since the API route uses uploadImage middleware
    const formData = new FormData();

    // Map frontend fields to backend fields
    if (data.name) formData.append('sp_name', data.name);
    if (data.email) formData.append('sp_email', data.email);
    if (data.mobile || data.phone) formData.append('sp_mobile', (data.mobile || data.phone || '').toString());
    if (data.sp_country_code || data.country_code) {
      formData.append('sp_country_code', (data.sp_country_code || data.country_code || '').toString());
    }
    if (data.fcm_token) formData.append('fcm_token', data.fcm_token);
    if (data.status) formData.append('sp_status', data.status);
    if (data.email_verified !== undefined) formData.append('email_verified', data.email_verified.toString());
    if (data.mobile_verified !== undefined) formData.append('mobile_verified', data.mobile_verified.toString());
    if (data.notification_enabled !== undefined) formData.append('notification_enabled', data.notification_enabled.toString());
    if (data.is_top_cleaner !== undefined) {
      formData.append('is_top_cleaner', data.is_top_cleaner ? 'Yes' : 'No');
    }

    // Add sp_id (required by backend)
    formData.append('sp_id', id.toString());

    // Handle password
    if (data.password) {
      formData.append('password', data.password);
    }

    try {
      console.log(`🔍 Trying to update staff at: ${API_BASE_URL}/staff/update`);
      console.log('📝 Update data keys:', Array.from(formData.keys()));

      const response = await fetch(`${API_BASE_URL}/staff/update`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Staff updated successfully`);

        // Handle response based on your API structure
        const userData = result.data || result;
        return {
          ...userData,
          id: userData.sp_id || userData.id,
          phone: userData.sp_mobile || userData.mobile || userData.phone
        };
      } else {
        const errorText = await response.text();
        console.log(`❌ Update failed: ${response.status} - ${errorText}`);
        throw new Error(`Update failed: ${response.status}`);
      }
    } catch (error) {
      console.log(`❌ Update failed:`, error);
      throw new Error('Failed to update staff member');
    }
  },

  /**
   * Delete a staff member
   */
  delete: async (id: number): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/staff/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ sp_id: id })
      });

      if (response.ok) {
        return;
      } else {
        const errorText = await response.text();
        console.log(`❌ Delete failed: ${response.status} - ${errorText}`);
        throw new Error(`Delete failed: ${response.status}`);
      }
    } catch (error) {
      console.log(`❌ Delete failed:`, error);
      throw new Error('Failed to delete staff member');
    }
  },

  /**
   * Get staff member by ID
   */
  getById: async (id: number): Promise<StaffMember> => {
    console.log('🔧 staffMember.ts getById method called with ID:', id);

    try {
      console.log(`🔍 Fetching staff by ID from: ${API_BASE_URL}/staff/get-by-id`);

      const response = await fetch(`${API_BASE_URL}/staff/get-by-id`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sp_id: id })
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Staff fetched successfully by ID`);

        // Handle response based on your API structure
        const userData = result.data || result;
        return {
          ...userData,
          id: userData.sp_id || userData.id,
          phone: userData.sp_mobile || userData.mobile || userData.phone
        };
      } else {
        const errorText = await response.text();
        console.log(`❌ Get staff by ID failed: ${response.status} - ${errorText}`);
        throw new Error(`Failed to get staff member: ${response.status}`);
      }
    } catch (error) {
      console.log(`❌ Error getting staff by ID:`, error);
      throw new Error('Failed to get staff member');
    }
  },

  /**
   * Update staff status
   */
  updateStatus: async (id: number, status: string): Promise<void> => {
    console.log('🔧 staffMember.ts updateStatus method called with ID:', id, 'Status:', status);

    try {
      const response = await fetch(`${API_BASE_URL}/staff/update-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ sp_id: id, sp_status: status })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.log(`❌ Update status failed: ${response.status} - ${errorText}`);
        throw new Error(`Update status failed: ${response.status}`);
      }
    } catch (error) {
      console.log(`❌ Update status failed:`, error);
      throw new Error('Failed to update staff status');
    }
  },

  /**
   * Resend OTP for staff verification
   */
  resendOtp: async (email: string): Promise<void> => {
    console.log('🔧 staffMember.ts resendOtp method called with email:', email);

    try {
      const response = await fetch(`${API_BASE_URL}/staff/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ sp_email: email })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.log(`❌ Resend OTP failed: ${response.status} - ${errorText}`);
        throw new Error(`Resend OTP failed: ${response.status}`);
      }
    } catch (error) {
      console.log(`❌ Resend OTP failed:`, error);
      throw new Error('Failed to resend OTP');
    }
  },

  /**
   * Verify staff OTP and activate account
   */
  verifyOtp: async (email: string, otp: string): Promise<void> => {
    console.log('🔧 staffMember.ts verifyOtp method called with email:', email);

    try {
      const response = await fetch(`${API_BASE_URL}/staff/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ sp_email: email, otp })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.log(`❌ Verify OTP failed: ${response.status} - ${errorText}`);
        throw new Error(`Verify OTP failed: ${response.status}`);
      }
    } catch (error) {
      console.log(`❌ Verify OTP failed:`, error);
      throw new Error('Failed to verify OTP');
    }
  },

  /**
   * Change staff password
   */
  changePassword: async (id: number, currentPassword: string, newPassword: string): Promise<void> => {
    console.log('🔧 staffMember.ts changePassword method called with ID:', id);

    try {
      const response = await fetch(`${API_BASE_URL}/staff/change-password`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          id,
          current_password: currentPassword,
          new_password: newPassword,
          new_password_confirmation: newPassword
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to change password' }));
        throw new Error(error.message || 'Failed to change password');
      }
    } catch (error) {
      console.log(`❌ Change password failed:`, error);
      throw new Error('Failed to change password');
    }
  },

  /**
   * Get staff payouts
   */
  getPayouts: async (staffId: number): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/bookings/getStaffPayouts`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        staff_id: staffId
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch payouts' }));
      throw new Error(error.message || 'Failed to fetch payouts');
    }

    return response.json();
  }
};

